import { useState, useMemo, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { AppTable } from '@/shared/ui/AppTable';
import {
  useDeploymentUsers,
  useCreateDeploymentUser,
  useUpdateDeploymentUser,
  useDeleteDeploymentUser,
  useBulkCreateDeploymentUsers,
} from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { UnsavedBar } from '@/shared/ui/UnsavedBar';
import { ModalShell } from '@/shared/ui/ModalShell';
import { FormField, fieldInputStyle } from '@/shared/ui/FormField';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { semantic, userRoleMeta, typography, radius } from '@/shared/theme/tokens';
import { usePermissions } from '@/entities/rbac/hooks/usePermissions';
import { useAuthStore } from '@/entities/session/model/authStore';
import { useToast, useApiErrorToast } from '@/shared/ui/Toast';
import { BulkUploadModal } from '@/shared/ui/BulkUploadModal';
import { AppSelect } from '@/shared/ui/AppSelect';

// Within a deployment, all managers assign client-tier roles only
const RIPIK_ROLE_OPTIONS = [
  { value: 'client_admin', label: 'Admin' },
  { value: 'client_user',  label: 'User' },
];
const CLIENT_ROLE_OPTIONS = [
  { value: 'client_admin', label: 'Admin' },
  { value: 'client_user',  label: 'User' },
];

// ── Role badge (read-only visual) ─────────────────────────────────────────────
function RoleBadge({ role, small }) {
  const m = userRoleMeta[role] ?? userRoleMeta.ripik_user;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: small ? '2px 8px' : '4px 10px',
      borderRadius: radius.sm,
      background: m.bg, border: `1px solid ${m.border}`,
      fontSize: small ? '10px' : '11px', fontWeight: typography.fontWeight.semibold, color: m.text, whiteSpace: 'nowrap',
      lineHeight: '1.4', flexShrink: 0,
    }}>
      {m.label}
    </span>
  );
}



// ── Main UsersTab ─────────────────────────────────────────────────────────────
export function UsersTab({ deploymentId, canManage }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { data: users = [], isLoading } = useDeploymentUsers(deploymentId);
  const updateUser = useUpdateDeploymentUser(deploymentId);
  const deleteUser = useDeleteDeploymentUser(deploymentId);
  const bulkCreateUser = useBulkCreateDeploymentUsers(deploymentId);
  const { isRipikAdmin } = usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const toast = useToast();
  const toastApiError = useApiErrorToast();
  const roleOptions = isRipikAdmin ? RIPIK_ROLE_OPTIONS : CLIENT_ROLE_OPTIONS;

  // Pending local role edits: userId → new role string
  const [pendingEdits, setPendingEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const hasUnsaved = Object.keys(pendingEdits).length > 0;

  const [userModal, setUserModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // ── Stable row id getter (avoids DataGrid crash when row.id is undefined) ───
  // Deterministic row id: prefer real ids, fall back to a stable composite of
  // email + deploymentId. Never use Math.random() — a fresh key each render
  // remounts the row and drops focus mid-edit.
  const getRowId = useCallback(
    (row) => row.id ?? row.userId ?? row.email ?? `${row.deploymentId ?? 'dep'}:${row.userName ?? 'row'}`,
    []
  );

  // ── Column defs ──────────────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const base = [
      {
        field: 'userName',
        headerName: 'Name',
        flex: 1.4,
        minWidth: 200,
        renderCell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', overflow: 'hidden' }}>
            <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}>
              {row.userName || '—'}
            </span>
            <span style={{ fontSize: typography.fontSize.xs, color: c.borderSubtle, flexShrink: 0 }}>·</span>
            <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, fontFamily: typography.fontFamily.mono, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {row.userId}
            </span>
          </div>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.4,
        minWidth: 180,
        renderCell: ({ value }) => (
          <span style={{ fontSize: typography.fontSize.md, color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}>{value}</span>
        ),
      },
      {
        field: 'role',
        headerName: 'Role (this deployment)',
        flex: 1.1,
        minWidth: 200,
        sortable: true,
        renderCell: ({ row }) => {
          const pending = pendingEdits[row.id ?? row.userId];
          const effectiveRole = pending ?? row.role;
          // canManage = ripik_admin or client_admin. Always show select for managed rows.
          // If the current user's roleOptions don't include this row's role (e.g. client_admin
          // viewing a ripik_admin row), show a read-only badge instead.
          const isEditable = canManage && roleOptions.some((r) => r.value === effectiveRole);
          return (
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
              {isEditable ? (
                <div style={{ minWidth: '120px', width: '100%', display: 'flex', alignItems: 'center' }}>
                  <AppSelect
                    options={roleOptions}
                    value={effectiveRole}
                    onChange={(newRole) => {
                      const uid = row.id ?? row.userId;
                      setPendingEdits((prev) => {
                        const next = { ...prev };
                        if (newRole === row.role) { delete next[uid]; }
                        else { next[uid] = newRole; }
                        return next;
                      });
                    }}
                    size="sm"
                    searchable={false}
                    clearable={false}
                    ariaLabel="Role"
                  />
                </div>
              ) : (
                <RoleBadge role={effectiveRole} />
              )}
            </div>
          );
        },
      },
    ];

    if (canManage) {
      base.push({
        field: '__actions',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const isSelf = (row.id ?? row.userId) === currentUserId;
          if (isSelf) return <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, fontStyle: 'italic' }}>You</span>;
          return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', width: '100%' }}>
              <button onClick={() => setUserModal({ mode: 'edit', user: row })} style={actionBtnStyle(c)} title="Edit">
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: c.textSubtle }}>edit</span>
              </button>
              <button onClick={() => setDeleteTarget(row)} style={{ ...actionBtnStyle(c), borderColor: semantic.errorBorder }} title="Remove">
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: semantic.error }}>person_remove</span>
              </button>
            </div>
          );
        },
      });
    }
    return base;
  }, [canManage, c, pendingEdits, roleOptions, currentUserId]);

  // ── Rows: merge pending role edits ───────────────────────────────────────────
  const rows = useMemo(
    () => users.map((u) => {
      const uid = u.id ?? u.userId;
      return pendingEdits[uid] ? { ...u, role: pendingEdits[uid] } : u;
    }),
    [users, pendingEdits]
  );

  // ── Save / Discard ────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(pendingEdits).map(([userId, role]) =>
          updateUser.mutateAsync({ userId, payload: { role } })
        )
      );
      const count = Object.keys(pendingEdits).length;
      toast({ message: `Role${count > 1 ? 's' : ''} updated` });
      setPendingEdits({});
    } catch (err) {
      toastApiError(err);
    } finally {
      setSaving(false);
    }
  }, [pendingEdits, updateUser, toast, toastApiError]);

  const handleDiscard = useCallback(() => setPendingEdits({}), []);

  async function handleDownload(userList) {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Users');
    ws.columns = [
      { header: 'userName', key: 'userName' },
      { header: 'email', key: 'email' },
      { header: 'role', key: 'role' },
    ];
    userList.forEach((u) => ws.addRow({ userName: u.userName ?? '', email: u.email ?? '', role: u.role ?? '' }));
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `users-${deploymentId}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  }

  const handleDelete = useCallback(async (user) => {
    try {
      await deleteUser.mutateAsync(user.id ?? user.userId);
      setDeleteTarget(null);
      toast({ message: `${user.userName || user.email} removed`, type: 'info' });
    } catch (err) {
      toastApiError(err);
    }
  }, [deleteUser, toast, toastApiError]);


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {hasUnsaved && (
        <UnsavedBar label="Unsaved role changes" onSave={handleSave} onDiscard={handleDiscard} saving={saving} />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>
          {users.length} user{users.length !== 1 ? 's' : ''}
        </span>
        <span style={{ flex: 1 }} />
        <PrimaryButton variant="secondary" icon="download" size="sm" onClick={() => handleDownload(users)}>
          Download
        </PrimaryButton>
        {canManage && (
          <>
            <PrimaryButton variant="secondary" icon="upload_file" size="sm" onClick={() => setBulkOpen(true)}>
              Bulk Upload
            </PrimaryButton>
            <PrimaryButton icon="person_add" size="sm" onClick={() => setUserModal({ mode: 'create' })}>
              Add User
            </PrimaryButton>
          </>
        )}
      </div>

      {/* DataGrid Pro container */}
      <div style={{
        flex: 1,
        minHeight: 0,
        background: c.bg,
        border: hasUnsaved ? `1.5px solid ${semantic.warningBorder}` : `1px solid ${c.border}`,
        borderRadius: radius.xl,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}>
        <AppTable
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          loading={isLoading}
          getRowClassName={({ row }) => {
            const uid = row.id ?? row.userId;
            const original = users.find((u) => (u.id ?? u.userId) === uid);
            return pendingEdits[uid] && original && pendingEdits[uid] !== original.role ? 'row--edited' : '';
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            pinnedColumns: { right: ['__actions'] },
          }}
          extraSx={{
            '& .MuiDataGrid-cell[data-field="role"]': { overflow: 'visible' },
            '& .MuiDataGrid-row.row--edited': { backgroundColor: `${semantic.warningBg} !important` },
            '& .MuiDataGrid-row.row--edited:hover': { backgroundColor: `${semantic.warningBg} !important` },
            '& .MuiDataGrid-row.row--edited .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row.row--edited .MuiDataGrid-cell--pinnedRight': {
              background: `${semantic.warningBg} !important`,
            },
          }}
        />
      </div>

      {/* Modals */}
      {userModal && (
        <UserFormModal
          mode={userModal.mode}
          user={userModal.user}
          deploymentId={deploymentId}
          roleOptions={roleOptions}
          isDark={isDark}
          onClose={() => setUserModal(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          user={deleteTarget}
          loading={deleteUser.isPending}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
          isDark={isDark}
        />
      )}
      {bulkOpen && (
        <BulkUploadModal
          title="Bulk Upload Users"
          subtitle="Upload a CSV file to add multiple users"
          entityLabel="User"
          requiredColumns={['userName', 'email', 'role']}
          filterRow={(r) => !!(r.userName || r.email)}
          previewColumns={[
            { key: 'userName', label: 'Name' },
            { key: 'email',    label: 'Email' },
            { key: 'role',     label: 'Role' },
          ]}
          sampleCsv={'userName,email,role\nJane Operator,jane@company.com,client_user\n'}
          sampleFilename="sample-users.csv"
          mutateAsync={(file) => bulkCreateUser.mutateAsync(file)}
          isPending={bulkCreateUser.isPending}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}

// ── UserFormModal ─────────────────────────────────────────────────────────────
function UserFormModal({ mode, user, deploymentId, roleOptions, isDark: _isDark, onClose }) {
  const c = useThemeColors();
  const createUser = useCreateDeploymentUser(deploymentId);
  const updateUser = useUpdateDeploymentUser(deploymentId);
  const toast = useToast();
  const toastApiError = useApiErrorToast();
  const pending = createUser.isPending || updateUser.isPending;

  const [form, setForm] = useState({
    userName: user?.userName ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    role: user?.role ?? (roleOptions[0]?.value ?? 'client_user'),
  });
  const [errors, setErrors] = useState({});

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.userName.trim()) e.userName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    // Phone optional; if given, must be E.164-ish (used for WhatsApp notifications).
    if (form.phone.trim() && !/^\+?[0-9]{8,15}$/.test(form.phone.trim().replace(/[\s-]/g, ''))) e.phone = 'Invalid phone (use E.164, e.g. +919812345678)';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const payload = { userName: form.userName, email: form.email, phone: form.phone.trim() || null, role: form.role };
    try {
      if (mode === 'create') {
        await createUser.mutateAsync(payload);
        toast({ message: `${form.userName || form.email} added` });
      } else {
        await updateUser.mutateAsync({ userId: user.id ?? user.userId, payload });
        toast({ message: `${form.userName || form.email} updated` });
      }
      onClose();
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <ModalShell
      onClose={onClose}
      icon="person"
      iconVariant="primary"
      title={mode === 'create' ? 'Add User' : 'Edit User'}
      subtitle={mode === 'create' ? 'Add a user to this deployment' : 'Update user details'}
      maxWidth="480px"
      footer={
        <>
          <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>Cancel</PrimaryButton>
          <PrimaryButton type="submit" form="user-form" disabled={pending} icon={mode === 'create' ? 'person_add' : 'check'}>
            {pending ? 'Saving…' : (mode === 'create' ? 'Add User' : 'Save')}
          </PrimaryButton>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <FormField label="Full Name" error={errors.userName} required>
          <input value={form.userName} onChange={(e) => setField('userName', e.target.value)} placeholder="Jane Operator" style={fieldInputStyle(c, !!errors.userName)} />
        </FormField>
        <FormField label="Email" error={errors.email} required>
          <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="jane@company.com" style={fieldInputStyle(c, !!errors.email)} />
        </FormField>
        <FormField label="Phone" error={errors.phone} hint="Optional — used for WhatsApp notifications (E.164, e.g. +919812345678)">
          <input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+919812345678" style={fieldInputStyle(c, !!errors.phone)} />
        </FormField>
        <FormField label="Role (this deployment)">
          <AppSelect
            options={roleOptions}
            value={form.role}
            onChange={(v) => setField('role', v)}
            size="sm"
            searchable={false}
            clearable={false}
            ariaLabel="Role (this deployment)"
          />
        </FormField>
      </form>
    </ModalShell>
  );
}

// ── ConfirmDeleteModal ────────────────────────────────────────────────────────
function ConfirmDeleteModal({ user, loading, onConfirm, onCancel, isDark: _isDark }) {
  const c = useThemeColors();
  return (
    <ModalShell
      onClose={onCancel}
      icon="person_remove"
      iconVariant="danger"
      title="Remove User"
      subtitle="This cannot be undone."
      maxWidth="380px"
      footer={
        <>
          <PrimaryButton variant="secondary" onClick={onCancel} disabled={loading}>Cancel</PrimaryButton>
          <PrimaryButton variant="danger" onClick={onConfirm} disabled={loading} icon="person_remove">
            {loading ? 'Removing…' : 'Remove'}
          </PrimaryButton>
        </>
      }
    >
      <p style={{ fontSize: typography.fontSize.md, color: c.textLabel, margin: '0 0 4px', lineHeight: 1.5 }}>
        Remove <strong style={{ color: c.textPrimary }}>{user.userName || user.email}</strong> from this deployment?
      </p>
    </ModalShell>
  );
}

function actionBtnStyle(c) {
  return {
    width: 28, height: 28, borderRadius: '7px',
    background: 'transparent', border: `1px solid ${c.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0, flexShrink: 0,
  };
}
